import {useEffect,useRef,useState} from 'react';
import {api} from '../../lib/api';
import {formatMoney} from '../lib/format';
import {useI18n} from '../lib/i18n';
import type {AppointmentResponse} from '../types';
export interface FinancialQuote {token:string;price_cents:number;deposit_cents:number;currency:string;requires_checkout:boolean;disclosure:string[];membership:{name:string;benefit:string;discount_cents:number;original_price_cents:number}|null;
 /** Payment pricing (0045). Equal figures when the business absorbs its processing cost. */
 pricing_mode?:'absorb'|'dual'|'card_fee';card_price_cents?:number;cash_price_cents?:number;price_kind?:'cash'|'card';cash_available?:boolean;
 /** Card processing fee (0048), when the business charges one on credit cards. */
 card_fee_bps?:number;card_fee_cents?:number}
export interface FinancialAcceptance {quote:FinancialQuote|null;accepted:boolean;ready:boolean;priceKind:'cash'|'card'}
/**
 * What this booking costs, and — when the business publishes a cash
 * discount (0045) — which of the two prices the customer is choosing.
 *
 * BOTH PRICES ARE ON SCREEN BEFORE ANYTHING IS CHOSEN. Choosing changes
 * the quote by asking the SERVER again with the chosen path: the total
 * below is always one the server calculated, never one this component
 * worked out, and the token that comes back is the one the booking is
 * made with.
 */
export function FinancialDisclosure({slug,query,onChange}:{slug:string;query:string;onChange:(v:FinancialAcceptance)=>void}){
 const {t,locale}=useI18n();
 // What the customer last PRESSED. The screen follows it immediately —
 // a radio that waits for a network round trip reads as broken — and the
 // server's answer corrects it if this business cannot honour that path.
 const [priceKind,setPriceKind]=useState<'cash'|'card'>('card');
 const [data,setData]=useState<{quote:FinancialQuote;ready:boolean}|null>(null),[accepted,setAccepted]=useState(false),[error,setError]=useState('');
 const [updating,setUpdating]=useState(false);
 const lastQuery=useRef<string|null>(null);
 useEffect(()=>{let live=true;
 // A different service, professional or time is a different booking and
 // starts again; changing only the payment path keeps what is on screen
 // while the new total is fetched.
 if(lastQuery.current!==query){lastQuery.current=query;setData(null);setAccepted(false);}
 setError('');setUpdating(true);onChange({quote:null,accepted:false,ready:false,priceKind});
 api<{quote:FinancialQuote;ready:boolean}>(`/public/${encodeURIComponent(slug)}/financial-quote?${query}&price_kind=${priceKind}`)
 .then(r=>{if(!live)return;setData(r);setUpdating(false);
  const served=r.quote.price_kind??'card';
  if(served!==priceKind)setPriceKind(served);
  onChange({quote:r.quote,accepted:r.quote.disclosure.length===0,ready:r.ready,priceKind:served});})
 .catch(e=>{if(live){setUpdating(false);setError(e instanceof Error?e.message:t('booking.terms_error'));}});
 return()=>{live=false;};},[slug,query,priceKind,onChange]);
 if(error)return <p className="error" role="alert">{error}</p>;
 if(!data)return <p aria-live="polite">{t('booking.checking_details')}</p>;
 const q=data.quote,money=(n:number)=>formatMoney(n,q.currency,locale);
 const dual=q.pricing_mode==='dual'&&(q.cash_price_cents??0)<(q.card_price_cents??0);
 // A card fee (0048) is disclosed as its own line — price, fee, total —
 // and is not a choice: the booking is made at the price, and the fee
 // applies only if a credit card is used at the appointment.
 const cardFee=q.pricing_mode==='card_fee'&&(q.card_fee_cents??0)>0;
 const feePercent=String((q.card_fee_bps??0)/100);
 return <section className="review" aria-label={t('booking.total_heading')}>
  <h3>{t('booking.total_heading')}</h3>
  {q.membership&&<p>{q.membership.name}: {q.membership.benefit==='included'?t('booking.member_included'):t('booking.member_saving',{amount:money(q.membership.discount_cents)})}. {t('booking.regular_price',{amount:money(q.membership.original_price_cents)})}</p>}
  {dual&&<div className="pay-choice">
   <p className="muted small">{t('booking.cash_saving_note')}</p>
   <ul className="price-pair">
    <li><span>{t('booking.cash_price')}</span> <strong>{money(q.cash_price_cents!)}</strong></li>
    <li><span>{t('booking.card_price')}</span> <strong>{money(q.card_price_cents!)}</strong></li>
   </ul>
   {q.cash_available!==false&&<fieldset className="pay-choice-options">
    <legend>{t('booking.how_to_pay_heading')}</legend>
    <label className={priceKind==='cash'?'pay-choice-option is-selected':'pay-choice-option'}>
     <input type="radio" name="price_kind" value="cash" checked={priceKind==='cash'} onChange={()=>setPriceKind('cash')}/>
     <span>{t('booking.pay_cash_option')} · <strong>{money(q.cash_price_cents!)}</strong></span>
    </label>
    <label className={priceKind==='card'?'pay-choice-option is-selected':'pay-choice-option'}>
     <input type="radio" name="price_kind" value="card" checked={priceKind==='card'} onChange={()=>setPriceKind('card')}/>
     <span>{t('booking.pay_card_option')} · <strong>{money(q.card_price_cents!)}</strong></span>
    </label>
   </fieldset>}
   <p className="small">{(q.price_kind??'card')==='cash'?t('booking.you_chose_cash'):t('booking.you_chose_card')}</p>
  </div>}
  {cardFee&&<div className="pay-choice" data-testid="card-fee-disclosure">
   <ul className="price-pair">
    <li><span>{t('booking.price')}</span> <strong>{money(q.cash_price_cents!)}</strong></li>
    <li><span>{t('booking.card_fee',{percent:feePercent})}</span> <strong>{money(q.card_fee_cents!)}</strong></li>
    <li><span>{t('booking.credit_card_total')}</span> <strong>{money(q.card_price_cents!)}</strong></li>
   </ul>
   <p className="muted small">{t('booking.card_fee_note',{percent:feePercent})}</p>
  </div>}
  <p aria-live="polite">{updating?t('booking.price_updating'):<>{t('booking.service_total',{amount:money(q.price_cents)})}{q.deposit_cents>0&&<> · {t('booking.due_now',{amount:money(q.deposit_cents)})} · {t('booking.remaining',{amount:money(q.price_cents-q.deposit_cents)})}</>}</>}</p>
  {q.disclosure.map((d,i)=><p key={i}>{d}</p>)}
  {q.disclosure.length>0&&<label><input type="checkbox" checked={accepted} onChange={e=>{setAccepted(e.target.checked);onChange({quote:q,accepted:e.target.checked,ready:data.ready,priceKind:q.price_kind??'card'});}}/> {t('booking.accept_terms')}</label>}
  {!data.ready&&<p role="alert">{t('booking.cannot_take_payment')}</p>}
 </section>;
}
export async function continueCheckout(slug:string,result:AppointmentResponse):Promise<boolean>{
 if(result.appointment.status!=='pending'||!result.appointment.financial_policy?.requires_checkout)return false;
 const a=result.appointment,url=`/public/${encodeURIComponent(slug)}/appointments/${a.id}/checkout?token=${encodeURIComponent(a.manage_token??'')}`;
 try{const response=await api<{url:string}>(url,{method:'POST',json:{}});window.location.assign(response.url);}catch{window.location.assign(`/${encodeURIComponent(slug)}/appointments/${a.id}?token=${encodeURIComponent(a.manage_token??'')}`);}
 return true;
}
